import type { ReportsVentasProductScope } from './reportsTypes';

/** Alcance canónico de Informes (Ventas + Personal). */
export type ReportsQueryScope = ReportsVentasProductScope & {
  customerId: string | null;
  cashierUserId: string | null;
  cashSessionId?: string | null;
  hasDiscount?: 'all' | 'yes' | 'no';
  promotionId?: string | null;
  promoCode?: string | null;
};

export const emptyReportsQueryScope = (): ReportsQueryScope => ({
  categoryId: null,
  itemType: 'all',
  search: null,
  productId: null,
  brandId: null,
  supplierId: null,
  customerId: null,
  cashierUserId: null,
  cashSessionId: null,
  hasDiscount: 'all',
  promotionId: null,
  promoCode: null,
});

export function ventasProductScopeToQueryScope(
  scope: ReportsVentasProductScope,
  customerId?: string | null,
  cashierUserId?: string | null,
  extras?: Partial<
    Pick<
      ReportsQueryScope,
      | 'cashSessionId'
      | 'hasDiscount'
      | 'promotionId'
      | 'promoCode'
    >
  >
): ReportsQueryScope {
  return {
    ...scope,
    customerId: customerId?.trim() ? customerId.trim() : null,
    cashierUserId: cashierUserId?.trim() ? cashierUserId.trim() : null,
    cashSessionId: extras?.cashSessionId?.trim() ? extras.cashSessionId.trim() : null,
    hasDiscount: extras?.hasDiscount ?? 'all',
    promotionId: extras?.promotionId?.trim() ? extras.promotionId.trim() : null,
    promoCode: extras?.promoCode?.trim() ? extras.promoCode.trim() : null,
  };
}

export function reportsQueryScopeHasFilters(scope: ReportsQueryScope | null | undefined): boolean {
  if (!scope) return false;
  return (
    scope.categoryId != null ||
    scope.itemType !== 'all' ||
    (scope.search?.trim() ?? '') !== '' ||
    (scope.productId?.trim() ?? '') !== '' ||
    (scope.brandId?.trim() ?? '') !== '' ||
    (scope.supplierId?.trim() ?? '') !== '' ||
    (scope.customerId?.trim() ?? '') !== '' ||
    (scope.cashierUserId?.trim() ?? '') !== '' ||
    (scope.cashSessionId?.trim() ?? '') !== '' ||
    (scope.hasDiscount ?? 'all') !== 'all' ||
    (scope.promotionId?.trim() ?? '') !== '' ||
    (scope.promoCode?.trim() ?? '') !== ''
  );
}

export type ReportsQueryScopeRpcParams = {
  p_category: string | null;
  p_item_type: string | null;
  p_search: string | null;
  p_product_id: string | null;
  p_brand: string | null;
  p_supplier_id: string | null;
  p_customer_id: string | null;
  p_cashier_user_id: string | null;
};

const RPC_SCOPE_NONE: ReportsQueryScopeRpcParams = {
  p_category: null,
  p_item_type: null,
  p_search: null,
  p_product_id: null,
  p_brand: null,
  p_supplier_id: null,
  p_customer_id: null,
  p_cashier_user_id: null,
};

function productRpcParams(scope: ReportsQueryScope): Omit<ReportsQueryScopeRpcParams, 'p_cashier_user_id'> {
  const productId = scope.productId?.trim() ? scope.productId.trim() : null;
  const search = scope.search?.trim() ? scope.search.trim() : null;
  const brand =
    scope.brandId != null && String(scope.brandId).trim() !== '' ? String(scope.brandId).trim() : null;
  const supplierId =
    scope.supplierId != null && String(scope.supplierId).trim() !== ''
      ? String(scope.supplierId).trim()
      : null;
  const customerId =
    scope.customerId != null && String(scope.customerId).trim() !== ''
      ? String(scope.customerId).trim()
      : null;
  const hasProductFilter =
    scope.categoryId != null ||
    scope.itemType !== 'all' ||
    search != null ||
    productId != null ||
    brand != null ||
    supplierId != null ||
    customerId != null;
  if (!hasProductFilter) {
    return {
      p_category: null,
      p_item_type: null,
      p_search: null,
      p_product_id: null,
      p_brand: null,
      p_supplier_id: null,
      p_customer_id: customerId,
    };
  }
  const itemArg = scope.itemType === 'all' ? null : scope.itemType;
  if (productId) {
    return {
      p_category: scope.categoryId ?? null,
      p_item_type: itemArg,
      p_product_id: productId,
      p_search: null,
      p_brand: brand,
      p_supplier_id: supplierId,
      p_customer_id: customerId,
    };
  }
  return {
    p_category: scope.categoryId ?? null,
    p_item_type: itemArg,
    p_search: search ?? null,
    p_product_id: null,
    p_brand: brand,
    p_supplier_id: supplierId,
    p_customer_id: customerId,
  };
}

/** Parámetros RPC alineados con `reports_filtered_sale_ids` / snapshot / personal_facts. */
export function reportsQueryScopeToRpcParams(scope?: ReportsQueryScope | null): ReportsQueryScopeRpcParams {
  if (!scope) return { ...RPC_SCOPE_NONE };
  const cashierUserId =
    scope.cashierUserId != null && String(scope.cashierUserId).trim() !== ''
      ? String(scope.cashierUserId).trim()
      : null;
  return {
    ...productRpcParams(scope),
    p_cashier_user_id: cashierUserId,
  };
}
